import React, { useState, useEffect, useRef } from "react";
import { X, Search, Plus, User } from "lucide-react";
import { customerService } from "../../api/customerService";
import AddCustomerModal from "../AddCustomerModal";
import { useNotification } from "../../contexts/NotificationContext";

export default function CustomerModal({ onClose, onSelect, isOpen }) {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const containerRef = useRef(null);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      fetchCustomers(1, false, "");
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchCustomers(1, false, search);
    }, 350); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [search]);

  const fetchCustomers = async (pageToFetch = 1, isAppend = false, querySearch = search) => {
    try {
      if (isAppend) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await customerService.list({
        page: pageToFetch,
        limit: 50,
        query: querySearch,
      });

      const items = response?.data || [];
      const meta = response?.meta || {};
      const totalCount = Number(meta.total ?? items.length);
      setTotal(totalCount);
      setPage(pageToFetch);

      setCustomers((prev) => {
        if (!isAppend) {
          return items;
        }
        const existingIds = new Set(prev.map((c) => c.id));
        const filteredNew = items.filter((c) => !existingIds.has(c.id));
        return [...prev, ...filteredNew];
      });

      if (items.length < 50) {
        setHasMore(false);
      } else if (totalCount > 0) {
        setHasMore(pageToFetch * 50 < totalCount);
      } else {
        setHasMore(true);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError("Gagal memuat data pelanggan");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    fetchCustomers(page + 1, true, search);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      if (!loading && !loadingMore && hasMore) {
        handleLoadMore();
      }
    }
  };

  const handleAddSubmit = async (data) => {
    try {
      const response = await customerService.create(data);
      const createdRaw = response?.data?.data || response?.data || response;
      const createdCustomer = {
        id: createdRaw?.id || createdRaw?.customer_id || "",
        name: createdRaw?.name || data?.name || "",
        phone_number:
          createdRaw?.phone_number ||
          createdRaw?.phone ||
          data?.phone_number ||
          data?.phone ||
          "",
        phone:
          createdRaw?.phone_number ||
          createdRaw?.phone ||
          data?.phone_number ||
          data?.phone ||
          "",
        notes: createdRaw?.notes || data?.notes || "",
        address: createdRaw?.address || "",
      };
      showNotification("success", "Pelanggan berhasil ditambahkan");
      setIsAddModalOpen(false);
      fetchCustomers(1, false, search);
      onSelect?.(createdCustomer);
      onClose?.();
    } catch (err) {
      console.error("Error creating customer:", err);
      showNotification(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Gagal menambahkan pelanggan",
      );
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm modal-backdrop-enter">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl h-[600px] flex flex-col modal-panel-enter">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Daftar Pelanggan
              </h2>
              <p className="text-sm text-gray-500">
                {total > 0
                  ? `Menampilkan ${customers.length} dari ${total} pelanggan`
                  : "Silahkan pilih pelanggan atau buat tambah baru"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cari Pelanggan..."
                className="w-full pl-9 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 font-medium">
              <Plus className="w-4 h-4" />
              Baru
            </button>
          </div>

          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto space-y-3 pr-2">
            {loading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Memuat pelanggan...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 mb-2">{error}</p>
                  <button
                    onClick={() => fetchCustomers(1, false, search)}
                    className="text-red-600 hover:text-red-800 text-sm underline">
                    Coba lagi
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && customers.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  Tidak ada pelanggan yang ditemukan
                </p>
              </div>
            )}

            {!loading &&
              !error &&
              customers.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => onSelect(customer)}
                  className="p-4 border rounded-xl hover:border-teal-500 hover:bg-teal-50 cursor-pointer transition-all flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 group-hover:bg-teal-100 group-hover:text-teal-600">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">{customer.name}</h4>
                    <p className="text-sm text-gray-500">
                      {customer.phone_number || customer.phone}
                    </p>
                    {(customer.address || customer.notes) && (
                      <p className="text-xs text-gray-400 mt-1">
                        {customer.address || customer.notes}
                      </p>
                    )}
                  </div>
                </div>
              ))}

            {loadingMore && (
              <div className="flex items-center justify-center py-3 gap-2 text-sm text-teal-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                <span>Memuat data pelanggan berikutnya...</span>
              </div>
            )}

            {hasMore && !loading && !loadingMore && (
              <div className="pt-2 pb-1 text-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="w-full py-2.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl border border-teal-200 transition-colors">
                  Muat Lebih Banyak Pelanggan
                </button>
              </div>
            )}

            {!hasMore && customers.length > 50 && (
              <p className="text-center text-xs text-gray-400 py-2">
                Semua pelanggan sudah ditampilkan ({customers.length})
              </p>
            )}
          </div>
        </div>
      </div>

      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddSubmit}
      />
    </>
  );
}

